import React, { useState } from 'react';
import { Drawer, List, ListItemButton, ListItemText, Toolbar, Collapse, IconButton, ListItemIcon } from '@mui/material';
import { ExpandLess, ExpandMore, Menu as MenuIcon, Tune, SwapHoriz, CloudSync, GridView, Rule, FactCheckOutlined, SmartToy } from '@mui/icons-material';

const drawerWidth = 240;

const NavigationPane = ({ onOptionClick, showAdditionalOptions, setShowAdditionalOptions }) => {
  const [openDataMigration, setOpenDataMigration] = useState(false);
  const [openDataModernization, setOpenDataModernization] = useState(false);
  const [openOptimization, setOpenOptimization] = useState(false);
  const [openCodeConversion, setOpenCodeConversion] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('');

  // Helper for left border and color
  const getSelectedStyles = (option, parent) => {
    const mainHeadings = ['Data Migration', 'Optimization', 'Modernization'];
    if (mainHeadings.includes(option) && !parent) {
      return {
        borderLeft: 'none',
        paddingLeft: '20px',
        color: 'white',
        fontWeight: 400,
      };
    }
    return {
      borderLeft: selectedOption === option ? '4px solid #FFD700' : 'none',
      paddingLeft: selectedOption === option ? '16px' : '20px',
      color: selectedOption === option ? '#FFD700' : 'white',
      fontWeight: selectedOption === option ? 500 : 400,
    };
  };

  // Unified handler for parent headings to ensure only one expanded at a time
  const handleParentClick = (heading) => {
    setSelectedOption(heading);
    if (heading === 'Data Migration') {
      setOpenDataMigration((prev) => !prev);
      setOpenDataModernization(false);
      setOpenOptimization(false);
    } else if (heading === 'Modernization') {
      setOpenDataModernization((prev) => !prev);
      setOpenDataMigration(false);
      setOpenOptimization(false);
    } else if (heading === 'Optimization') {
      setOpenOptimization((prev) => !prev);
      setOpenDataMigration(false);
      setOpenDataModernization(false);
    }
  };

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    if (onOptionClick) {
      onOptionClick(option);
    }
  };

  const drawerContent = (
    <>
      <div style={{ height: 28 }} />
      <List>
        {/* Data Migration */}
        <ListItemButton
          onClick={() => handleParentClick('Data Migration')}
          sx={getSelectedStyles('Data Migration')}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: '40px' }}>
            <SwapHoriz />
          </ListItemIcon>
          <ListItemText primary="Data Migration" sx={{ color: 'white' }} />
          {openDataMigration ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
        </ListItemButton>
        <Collapse in={openDataMigration} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {['NoSQL -> RDBMS', 'RDBMS -> NoSQL', 'RDBMS -> RDBMS'].map((subOption) => (
              <ListItemButton
                key={subOption}
                sx={{ pl: 4, ...getSelectedStyles(subOption, 'Data Migration') }}
                onClick={() => handleOptionClick(subOption)}
                selected={selectedOption === subOption}
              >
                <ListItemText
                  primary={subOption}
                  sx={{ color: selectedOption === subOption ? '#FFD700' : 'white', fontSize: '0.92rem' }}
                  primaryTypographyProps={{ fontSize: '0.92rem' }}
                />
              </ListItemButton>
            ))}
            {/* Informatica -> Python/Pyspark */}
            <ListItemButton
              sx={{ pl: 4, ...getSelectedStyles('Informatica -> Python/Pyspark', 'Data Migration') }}
              onClick={() => handleOptionClick('Informatica -> Python/Pyspark')}
              selected={selectedOption === 'Informatica -> Python/Pyspark'}
            >
              <ListItemText
                primary={
                  <span style={{ color: selectedOption === 'Informatica -> Python/Pyspark' ? '#FFD700' : 'white', fontSize: '0.92rem' }}>
                    Informatica XML -&gt; Python
                  </span>
                }
                sx={{ fontSize: '0.92rem' }}
                primaryTypographyProps={{ fontSize: '0.92rem' }}
              />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Modernization */}
        <ListItemButton
          onClick={() => handleParentClick('Modernization')}
          sx={getSelectedStyles('Modernization')}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: '40px' }}>
            <CloudSync />
          </ListItemIcon>
          <ListItemText primary="Modernization" sx={{ color: 'white' }} />
          {openDataModernization ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
        </ListItemButton>
        <Collapse in={openDataModernization} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {/* Code Conversion ListItem */}
            <ListItemButton
              sx={{ pl: 4, ...getSelectedStyles('Code Conversion', 'Modernization') }}
              onClick={() => {
                setSelectedOption('Code Conversion');
                setOpenCodeConversion((prev) => !prev);
              }}
              selected={selectedOption === 'Code Conversion'}
            >
              <ListItemText primary="Code Conversion" sx={{ color: selectedOption === 'Code Conversion' ? '#FFD700' : 'white' }} />
              {openCodeConversion ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
            </ListItemButton>
            <Collapse in={openCodeConversion} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton
                  sx={{ pl: 8, ...getSelectedStyles('Data Model & DDL', 'Modernization') }}
                  onClick={() => handleOptionClick('Data Model & DDL')}
                  selected={selectedOption === 'Data Model & DDL'}
                >
                  <ListItemText
                    primary="Data Model & DDL"
                    sx={{ color: selectedOption === 'Data Model & DDL' ? '#FFD700' : 'white', fontSize: '0.92rem' }}
                    primaryTypographyProps={{ fontSize: '0.92rem' }}
                  />
                </ListItemButton>
                <ListItemButton
                  sx={{ pl: 8, ...getSelectedStyles('SQL, PL/SQL', 'Modernization') }}
                  onClick={() => handleOptionClick('SQL, PL/SQL')}
                  selected={selectedOption === 'SQL, PL/SQL'}
                >
                  <ListItemText
                    primary="SQL, PL/SQL"
                    sx={{ color: selectedOption === 'SQL, PL/SQL' ? '#FFD700' : 'white', fontSize: '0.92rem' }}
                    primaryTypographyProps={{ fontSize: '0.92rem' }}
                  />
                </ListItemButton>
              </List>
            </Collapse>
            {/* Existing Modernization options */}
            {['Test Script Generation', 'Test Data Generation'].map((subOption) => (
              <ListItemButton
                key={subOption}
                sx={{ pl: 4, ...getSelectedStyles(subOption, 'Modernization') }}
                onClick={() => handleOptionClick(subOption)}
                selected={selectedOption === subOption}
              >
                <ListItemText
                  primary={subOption}
                  sx={{ color: selectedOption === subOption ? '#FFD700' : 'white', fontSize: '0.92rem' }}
                  primaryTypographyProps={{ fontSize: '0.92rem' }}
                />
              </ListItemButton>
            ))}
            <ListItemButton
              sx={{ pl: 4, ...getSelectedStyles('Scheduler', 'Modernization') }}
              onClick={() => handleOptionClick('Scheduler')}
              selected={selectedOption === 'Scheduler'}
            >
              <ListItemText
                primary={
                  <span style={{ color: selectedOption === 'Scheduler' ? '#FFD700' : 'white', fontSize: '0.92rem' }}>
                    Scheduler <sup style={{ fontSize: '0.7rem', color: '#FFD700' }}>*Coming Soon</sup>
                  </span>
                }
                sx={{ color: selectedOption === 'Scheduler' ? '#FFD700' : 'white' }}
              />
            </ListItemButton>
          </List>
        </Collapse>

        {/* Optimization */}
        <ListItemButton
          onClick={() => handleParentClick('Optimization')}
          sx={getSelectedStyles('Optimization')}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: '40px' }}>
            <Tune />
          </ListItemIcon>
          <ListItemText primary="Optimization" sx={{ color: 'white' }} />
          {openOptimization ? <ExpandLess sx={{ color: 'white' }} /> : <ExpandMore sx={{ color: 'white' }} />}
        </ListItemButton>
        <Collapse in={openOptimization} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {['Documentation', 'Tuning and Suggestions'].map((subOption) => (
              <ListItemButton
                key={subOption}
                sx={{ pl: 4, ...getSelectedStyles(subOption, 'Optimization') }}
                onClick={() => handleOptionClick(subOption)}
                selected={selectedOption === subOption}
              >
                <ListItemText
                  primary={subOption}
                  sx={{ color: selectedOption === subOption ? '#FFD700' : 'white', fontSize: '0.92rem' }}
                  primaryTypographyProps={{ fontSize: '0.92rem' }}
                />
              </ListItemButton>
            ))}
          </List>
        </Collapse>

        {/* Elevate Bot */}
        {['Elevate Bot'].map((text) => (
          <ListItemButton
            key={text}
            onClick={() => handleOptionClick(text)}
            sx={getSelectedStyles(text)}
            selected={selectedOption === text}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: '40px' }}>
              <SmartToy />
            </ListItemIcon>
            <ListItemText primary={text} sx={{ color: selectedOption === text ? '#FFD700' : 'white' }} />
          </ListItemButton>
        ))}
      </List>
    </>
  );

  return (
    <>
      {/* Hamburger menu for small screens */}
      <IconButton
        onClick={toggleDrawer}
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          left: 10,
          top: 70,
          zIndex: 1300,
          color: 'gray'
        }}
      >
        <MenuIcon />
      </IconButton>

      {/* Temporary Drawer for small screens */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            backgroundColor: 'rgba(1, 63, 114, 1)',
            color: 'white',
            zIndex: 1300,
            top: '64px',
            width: drawerWidth,
            boxSizing: 'border-box',
            height: 'calc(100vh - 64px - 7vh)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Permanent Drawer for medium and up */}
      <Drawer
        variant="permanent"
        anchor="left"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            backgroundColor: 'rgba(1, 63, 114, 1)',
            color: 'white',
            zIndex: 1000,
            top: '64px',
            width: drawerWidth,
            boxSizing: 'border-box',
            height: 'calc(100vh - 64px - 7vh)',
            borderRight: 0,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default NavigationPane;